import { clsx } from "clsx";
import { useLocation } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import useNavStore from "common/hooks/useNavStore";
import type { NavItemType } from "common/types";

import DesktopNav from "./DesktopNav";

import type { JSX, ReactNode } from "react";

interface DesktopLayoutProps {
  navItems: NavItemType[];
  children: ReactNode;
  bannersComponent: JSX.Element;
}

export default function DesktopLayout({
  navItems,
  children,
  bannersComponent,
}: DesktopLayoutProps) {
  const { isNavExpanded, setIsNavExpanded } = useNavStore();
  const { pathname } = useLocation();

  const isSettingsPage = pathname.includes("/settings");

  return (
    <div className="overflow-hidden flex h-dvh bg-[linear-gradient(132deg,#fff_-4.58%,#f5f6f9_42.09%,#dce1ea_85.29%)]">
      <RenderIf condition={!isSettingsPage}>
        <DesktopNav
          isExpanded={isNavExpanded}
          setIsExpanded={setIsNavExpanded}
          navItems={navItems}
        />
      </RenderIf>
      <div
        className={clsx(
          "flex flex-col px-2 flex-1 transition-[width]",
          isNavExpanded ? "w-[calc(100%-216px)]" : "w-[calc(100%-52px)]",
        )}
      >
        <RenderIf condition={!isSettingsPage}>
          <button
            aria-label="expand"
            className={clsx(
              "w-2 h-full absolute z-10",
              isNavExpanded ? "cursor-w-resize" : "cursor-e-resize",
            )}
            type="button"
            onClick={() => setIsNavExpanded(!isNavExpanded)}
          />
        </RenderIf>

        {bannersComponent && (
          <section
            aria-label="Banners"
            className="peer z-10 mt-2 transition-[padding] duration-300 empty:hidden"
          >
            {bannersComponent}
          </section>
        )}

        <main className="flex flex-col py-2 relative pt-2 h-full peer-[:not(:empty)]:h-[calc(100%-50px)]">
          {children}
        </main>
      </div>
    </div>
  );
}
